import React, { ChangeEvent, useEffect, useState } from "react";
import { Link, useHistory, useParams, withRouter } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import SwiperCore, { Autoplay } from "swiper";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";

import {
  getSubject,
  uploadSubjectContentImage,
  uploadSubjectHomeImage,
} from "../../store/actions";
import { State, SubjectDto, UserProfileDto } from "../../interfaces";
import i18n from "../../i18n";
import { AddEditSubjectModal } from "./";
import {
  OrganizingLayoutPage,
  LecturesLayoutPage,
  SubjectNotesPage,
  FlashCardLayoutPage,
  SkillsLayoutPage,
  QuestionsLayoutPage,
} from "../";
import { IMAGE_ACCEPT } from "../../constants";
import { SubjectTabMenu } from "../../components";
import { CourseEnum, RoleEnum } from "../../enums";
import profileBg from "../../assets/images/no image-bg.png";

export const SubjectDetailPageComponent = () => {
  //#region hooks
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams<any>();

  //#endregion

  //#region redux state
  const { response: subject } = useSelector(
    (state: any): State<SubjectDto> => state.subject.subject
  );
  const {
    loading: homeImageUploading,
    error: homeImageUploadingError,
    success: homeImageUploadingSuccess,
  } = useSelector((state: any): State<File> => state.subject.homeImage);

  const { loading: contentImageUploading, error: contentImageUploadingError } =
    useSelector((state: any): State<File> => state.subject.contentImage);

  const { response: profile } = useSelector(
    (state: any): State<UserProfileDto> => state.account.userProfile
  );

  //#endregion

  //#region internal state

  const [editingSubjectId, setEditingSubjectId] = useState<string | null>(null);
  const [showAddEditSubjectModal, setShowAddEditSubjectModal] =
    useState<boolean>(false);
  const [activeTab, setActiveTab] = useState("0");
  //#endregion

  document.title = `${t("documentTitle", { pageName: "subject" })}`;

  //#region handlers

  SwiperCore.use([Autoplay]);

  const toggleTab = (tab: any) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const onEditSubject = (id: string) => {
    setEditingSubjectId(id);
    setShowAddEditSubjectModal(true);
  };

  const onChangeSubjectHomeImage = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const imageFile = files[0];
      if (
        !imageFile.name.match(
          /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF)$/
        )
      ) {
        toast.error(t("selectValidImage"), {
          autoClose: 3000,
        });
        return false;
      }
      dispatch(uploadSubjectHomeImage(imageFile, history, subject?.id));
    }
  };

  const onChangeSubjectContentImage = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const imageFile = files[0];
      if (
        !imageFile.name.match(
          /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF|)$/
        )
      ) {
        toast.error(t("selectValidImage"), {
          autoClose: 3000,
        });
        return false;
      }
      dispatch(uploadSubjectContentImage(imageFile, history, subject?.id));
    }
  };

  const getLink = () => {
    try {
      const courseName =
        subject?.subjectCourseDuration[0]?.courseDuration?.course?.name?.en;
      const gradeId =
        subject?.subjectCourseDuration[0]?.courseDuration?.grade?.id;
      if (courseName) {
        if (courseName.toLowerCase() === "first course") {
          if (gradeId) {
            return `${CourseEnum.FIRST_COURSE}#${gradeId}`;
          } else {
            return CourseEnum.FIRST_COURSE;
          }
        } else if (courseName.toLowerCase() === "second course") {
          if (gradeId) {
            return `${CourseEnum.SECOND_COURSE}#${gradeId}`;
          } else {
            return CourseEnum.SECOND_COURSE;
          }
        }
      }
    } catch (exception) {
      return CourseEnum.FIRST_COURSE;
    }
  };

  //#endregion

  //#region effects

  useEffect(() => {
    if (id) {
      dispatch(getSubject(id, history));
    } else {
      history.goBack();
    }
  }, [id]);

  useEffect(() => {
    if (!homeImageUploading && homeImageUploadingSuccess) {
    }
  }, [homeImageUploading]);

  useEffect(() => {
    const hash = history.location.hash;
    if (hash) {
      switch (hash) {
        case "#organizing":
          setActiveTab("0");
          break;
        case "#questions":
          setActiveTab("1");
          break;
        case "#skills":
          setActiveTab("2");
          break;
        case "#notes":
          setActiveTab("3");
          break;
        case "#flash-cards":
          setActiveTab("4");
          break;
        case "#lectures":
          setActiveTab("5");
          break;
        default:
          setActiveTab("0");
          break;
      }
    }
  }, []);

  useEffect(() => {
    if (!homeImageUploading && homeImageUploadingError)
      toast.error(homeImageUploadingError, { autoClose: 3000 });
  }, [homeImageUploading]);

  useEffect(() => {
    if (!contentImageUploading && contentImageUploadingError)
      toast.error(contentImageUploadingError, { autoClose: 3000 });
  }, [contentImageUploading]);

  //#endregion

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <Container fluid>
          <div className="profile-foreground position-relative mx-n4 mt-n4">
            <div className="profile-wid-bg">
              <img
                src={subject?.contentImage?.path || profileBg}
                alt=""
                className="profile-wid-img"
              />
            </div>
          </div>
          <div className="pt-4 mb-4 mb-lg-3 pb-lg-4">
            <Row className="g-4">
              <div className="col-auto">
                <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                  <img
                    src={subject?.homeImage?.path || profileBg}
                    className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                    alt="user-profile"
                  />
                  {profile &&
                    profile.role &&
                    profile?.role.key == RoleEnum.SUPER_ADMIN && (
                      <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                        <Input
                          id="subject_home_image"
                          type="file"
                          className="profile-img-file-input"
                          accept={IMAGE_ACCEPT}
                          onChange={onChangeSubjectHomeImage}
                          disabled={homeImageUploading}
                        />
                        <Label
                          htmlFor="subject_home_image"
                          className="profile-photo-edit avatar-xs"
                        >
                          <span className="avatar-title rounded-circle bg-light text-body">
                            {homeImageUploading && <Spinner />}
                            {!homeImageUploading && (
                              <i className="ri-camera-fill"></i>
                            )}
                          </span>
                        </Label>
                      </div>
                    )}
                </div>
              </div>
              <div className="col-sm-12 col-md mt-sm-0 mt-lg-5 mt-md-5">
                <div className="p-2">
                  <div className="hstack text-white-50 gap-1">
                    <div
                      style={{
                        display: "flex",
                        alignSelf: "flex-start",
                        marginLeft: 1,
                      }}
                    >
                      <Link to={`../${getLink()}`}>
                        <span className="text-light bold">
                          {i18n.language === "ar"
                            ? subject?.subjectCourseDuration[0]?.courseDuration
                                ?.course?.name?.ar
                            : subject?.subjectCourseDuration[0]?.courseDuration
                                ?.course?.name?.en}
                        </span>
                      </Link>
                    </div>

                    {i18n.language === "ar" ? (
                      <i className="ri-arrow-left-s-line"></i>
                    ) : (
                      <i className="ri-arrow-right-s-line"></i>
                    )}
                    <div
                      className="me-2"
                      style={{ display: "flex", alignSelf: "flex-start" }}
                    >
                      <span className="text-mute bold">
                        {i18n.language === "ar"
                          ? subject?.subjectCourseDuration[0]?.courseDuration
                              ?.grade?.name?.ar
                          : subject?.subjectCourseDuration[0]?.courseDuration
                              ?.grade?.name?.en}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-white mt-1">
                    {i18n.language === "ar"
                      ? subject?.name?.ar
                      : subject?.name.en}
                  </h3>
                  <h6 className="text-white mt-1">
                    {subject?.isActive ? t("active") : t("inActive")}
                  </h6>
                </div>
              </div>
            </Row>
          </div>

          <Row>
            <Col lg={12}>
              <div>
                <div className="d-flex">
                  <SubjectTabMenu toggleTab={toggleTab} activeTab={activeTab} />
                  {profile &&
                    profile.role &&
                    profile?.role.key == RoleEnum.SUPER_ADMIN && (
                      <div className="flex-shrink-0">
                        {subject && (
                          <React.Fragment>
                            <Input
                              id="profile-foreground-img-file-input"
                              type="file"
                              className="profile-foreground-img-file-input d-none"
                              accept={IMAGE_ACCEPT}
                              onChange={onChangeSubjectContentImage}
                              disabled={contentImageUploading}
                            />
                            <Label
                              htmlFor="profile-foreground-img-file-input"
                              className="profile-photo-edit btn btn-light mb-0 mx-1"
                            >
                              {!contentImageUploading && (
                                <i className="ri-image-edit-line align-bottom me-1"></i>
                              )}
                              {contentImageUploading && (
                                <Spinner
                                  size={"sm"}
                                  className="align-center"
                                  style={{
                                    marginBottom: -1,
                                    marginRight: 3,
                                    marginLeft: 3,
                                  }}
                                />
                              )}
                              <span className="d-none d-lg-inline-block">
                                {t("uploadCover")}
                              </span>
                            </Label>
                            <Button
                              onClick={() => onEditSubject(subject.id)}
                              className="btn btn-success"
                            >
                              <i className="ri-edit-box-line align-bottom"></i>{" "}
                              <span className="d-none d-lg-inline-block">
                                {t("editItem", { item: "subject" })}
                              </span>
                            </Button>
                          </React.Fragment>
                        )}
                      </div>
                    )}
                </div>

                <TabContent activeTab={activeTab} className="pt-4">
                  {activeTab === "0" && (
                    <TabPane tabId="0">
                      <Card style={{ minHeight: "50vh" }}>
                        <CardBody>
                          <Row>
                            {subject && subject.id && (
                              <OrganizingLayoutPage subjectId={subject?.id} />
                            )}
                          </Row>
                        </CardBody>
                      </Card>
                    </TabPane>
                  )}
                  {activeTab === "1" && (
                    <TabPane tabId="1">
                      <Card style={{ minHeight: "50vh" }}>
                        <CardBody>
                          <Row>
                            {subject && subject.id && (
                              <QuestionsLayoutPage subjectId={subject?.id} />
                            )}
                          </Row>
                        </CardBody>
                      </Card>
                    </TabPane>
                  )}

                  {activeTab === "2" && (
                    <TabPane tabId="2">
                      <Card style={{ minHeight: "50vh" }}>
                        <CardBody>
                          <Row>
                            {subject && subject.id && (
                              <SkillsLayoutPage subjectId={subject?.id} />
                            )}
                          </Row>
                        </CardBody>
                      </Card>
                    </TabPane>
                  )}

                  {activeTab === "3" && (
                    <TabPane tabId="3">
                      <Card style={{ minHeight: "50vh" }}>
                        <CardBody>
                          <Row>
                            {subject && subject.id && (
                              <SubjectNotesPage subjectId={subject.id} />
                            )}
                          </Row>
                        </CardBody>
                      </Card>
                    </TabPane>
                  )}

                  {activeTab === "4" && (
                    <TabPane tabId="4">
                      <Card style={{ minHeight: "50vh" }}>
                        <CardBody>
                          <Row>
                            {subject && subject.id && (
                              <FlashCardLayoutPage subjectId={subject?.id} />
                            )}
                          </Row>
                        </CardBody>
                      </Card>
                    </TabPane>
                  )}
                  {activeTab === "5" && (
                    <TabPane tabId="5">
                      <Card style={{ minHeight: "50vh" }}>
                        <CardBody>
                          <Row>
                            {subject && subject.id && (
                              <LecturesLayoutPage subjectId={subject?.id} />
                            )}
                          </Row>
                        </CardBody>
                      </Card>
                    </TabPane>
                  )}
                </TabContent>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      {editingSubjectId && (
        <AddEditSubjectModal
          gradeId={
            subject?.subjectCourseDuration[0]?.courseDuration?.grade.id || ""
          }
          courseId={
            subject?.subjectCourseDuration[0]?.courseDuration?.course.id || ""
          }
          id={editingSubjectId}
          show={showAddEditSubjectModal}
          onCloseClick={() => {
            setShowAddEditSubjectModal(false);
            setEditingSubjectId(null);
            dispatch(getSubject(id, history));
          }}
        />
      )}
    </React.Fragment>
  );
};
export const SubjectDetailPage = withRouter(SubjectDetailPageComponent);
